import React, { useContext, useEffect, useState } from "react";
import { 
    Avatar, Box, Button, Card, Container, Grid, IconButton, List, 
    ListItem, ListItemAvatar, Divider, ListItemText, Typography, 
    useTheme, Stack, Tooltip 
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faCopy, faLink, faQrcode, faStore, faTags 
} from "@fortawesome/free-solid-svg-icons";

// Contextos y Componentes
import UserContext from "../../navigation/context/userContext";
import ResponseComponent from "../../components/responseComponent";
import QUDAreaChart from "../../components/charts/AreaChart";
import { getViews } from "../../controllers/company/AnalyticsController";
import { domain, imagesURL } from "../../apiRoutes/apiBase";

// Imágenes
import welcome1 from '../../assets/images/dashboard/welcome1.png';

export default function CompanyHome() {
    const { userData } = useContext(UserContext);
    const theme = useTheme();
    const history = useHistory();

    const [date, setDate] = useState(Date.now());
    const [dailyStatistics, setDailyStatistics] = useState([]);
    const [merchantTotalViews, setMerchantTotalViews] = useState(0);
    const [menuTotalViews, setMenuTotalViews] = useState(0);
    const [topProducts, setTopProducts] = useState([]);

    const [show, setShow] = useState(false);
    const [success, setSuccess] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const interval = setInterval(() => setDate(new Date()), 1000);
        handleGetViewsAnalytics();
        return () => clearInterval(interval);
    }, []);

    const handleGetViewsAnalytics = async () => {
        const response = await getViews('merchant', userData.entity.id);
        if (response.success === true) {
            setDailyStatistics(response.data.dailyStatisticsMix);
            setMerchantTotalViews(response.data.merchantTotalViews);
            setMenuTotalViews(response.data.menuTotalViews);
            setTopProducts(response.data.topProducts);
        }
    }

    const redirect = (route) => history.push(route);

    const handleCopy = (text) => {
        const url = `${domain}merchants/${userData.entity.uuid}/${text}`;
        navigator.clipboard.writeText(url);
        setSuccess(true);
        setMessage('Enlace copiado exitosamente');
        setShow(true);
    }

    // Arreglo limpio sin iconos para los botones delineados
    const quickActions = [
        { label: 'Perfil', path: '/profile' },
        { label: 'Productos', path: '/products' },
        { label: 'Eventos', path: '/events' },
        { label: 'Suscripciones', path: '/subscriptions' },
        { label: 'Pedidos', path: '/orders' },
        { label: 'Reservas', path: '/reservations' }
    ];

    const shareLinks = [
        { label: 'Landing Page', type: 'profile' },
        { label: 'Catálogo', type: 'menu' },
        { label: 'Reseñas', type: 'review' },
    ];

    // Estilo con bordes definidos (Restaurados)
    const cardStyle = {
        elevation: 0,
        sx: { 
            border: '1px solid #e0e0e0', // Borde sólido y visible
            borderRadius: 3, 
            height: '100%', 
            p: 3,
            bgcolor: '#ffffff'
        }
    };

    return (
        <Container maxWidth='xl' sx={{ py: 4 }}>
            <ResponseComponent
                show={show}
                success={success}
                message={message}
                actionText='Cerrar'
                onClick={() => setShow(false)}
            />

            {/* SECCIÓN 1: BIENVENIDA Y ENLACES */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} lg={8}>
                    <Card {...cardStyle} sx={{ ...cardStyle.sx, position: 'relative', overflow: 'hidden' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8} zIndex={1}>
                                <Typography variant="h5" fontWeight={700} sx={{ color: '#222' }} gutterBottom>
                                    ¡Bienvenido, {userData.entity.last_name || userData.entity.name}! 
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', mb: 3, lineHeight: 1.6 }}>
                                    Estamos felices de tenerte aquí. Desde este panel podrás gestionar tus productos, eventos, suscripciones y pedidos de manera fácil y rápida.
                                </Typography>
                                
                                <Typography variant="caption" sx={{ color: '#999', fontWeight: 700, letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
                                    ACCESOS RÁPIDOS
                                </Typography>
                                
                                <Grid container spacing={1.5}>
                                    {quickActions.map((action, index) => (
                                        <Grid item xs={6} sm={4} key={index}>
                                            {/* Botones Outlined sin icono, centrados */}
                                            <Button 
                                                fullWidth 
                                                variant="outlined"
                                                onClick={() => redirect(action.path)}
                                                sx={{ 
                                                    justifyContent: 'center', 
                                                    textTransform: 'none',
                                                    color: 'text.primary',
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: 2,
                                                    py: 1,
                                                    fontSize: '0.9rem',
                                                    fontWeight: 600,
                                                    '&:hover': { 
                                                        bgcolor: 'primary.50', 
                                                        borderColor: 'primary.main', 
                                                        color: 'primary.main' 
                                                    }
                                                }}
                                            >
                                                {action.label}
                                            </Button>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                            
                            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                                <img alt="Welcome" src={welcome1} style={{ width: '100%', maxWidth: 180, objectFit: 'contain', opacity: 0.9 }} />
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Card {...cardStyle}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Avatar variant='rounded' sx={{ width: 40, height: 40, bgcolor: '#f0f4f8', color: '#1976d2', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faLink} size="sm" />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ color: '#222' }}>Tus Enlaces</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>Compártelos con tus clientes</Typography>
                            </Box>
                        </Stack>

                        <List disablePadding>
                            {shareLinks.map((link, idx) => (
                                <React.Fragment key={idx}>
                                    <ListItem 
                                        disablePadding 
                                        sx={{ py: 1 }}
                                        secondaryAction={
                                            <Tooltip title="Copiar enlace" placement="top">
                                                <IconButton edge="end" onClick={() => handleCopy(link.type)} sx={{ color: '#888', '&:hover': { color: 'primary.main', bgcolor: '#f0f4f8' } }}>
                                                    <FontAwesomeIcon icon={faCopy} size="sm" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <ListItemText 
                                            primary={link.label} 
                                            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: '#444' }}
                                            secondary={`${domain}merchants/...`}
                                            secondaryTypographyProps={{ noWrap: true, sx: { maxWidth: 180, fontSize: '0.75rem', color: '#999' } }}
                                        />
                                    </ListItem>
                                    {idx !== shareLinks.length - 1 && <Divider sx={{ borderColor: '#e0e0e0' }} />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Card>
                </Grid>
            </Grid>

            {/* SECCIÓN 2: ANALÍTICAS */}
            <Typography variant="h6" fontWeight={700} mb={2} sx={{ color: '#222' }}>
                Resumen de Actividad
            </Typography>
            
            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    <Grid container spacing={3}>
                        
                        {/* KPI 1 */}
                        <Grid item xs={12} sm={6}>
                            <Card {...cardStyle} sx={{ ...cardStyle.sx, p: 2.5 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f0f4f8', color: '#1976d2', borderRadius: 2 }}>
                                        <FontAwesomeIcon icon={faStore} size="sm" />
                                    </Avatar>
                                    <Box flexGrow={1}>
                                        <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Perfil Comercial
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                            {merchantTotalViews}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Card>
                        </Grid>

                        {/* KPI 2 */}
                        <Grid item xs={12} sm={6}>
                            <Card {...cardStyle} sx={{ ...cardStyle.sx, p: 2.5 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 2 }}>
                                        <FontAwesomeIcon icon={faQrcode} size="sm" />
                                    </Avatar>
                                    <Box flexGrow={1}>
                                        <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Catálogo Digital
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                            {menuTotalViews}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Card>
                        </Grid>
    
                        {/* Gráfica */}
                        <Grid item xs={12}>
                            <Card {...cardStyle} sx={{ ...cardStyle.sx, p: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} mb={2} px={1} sx={{ color: '#555' }}>
                                    Tráfico de los últimos 7 días
                                </Typography>
                                {dailyStatistics.length !== 0 ? (
                                    <Box sx={{ height: 280, width: '100%' }}>
                                        <QUDAreaChart
                                            data={dailyStatistics}
                                            dataKeyX={'date'}
                                            dataKeyY={'menu'}
                                            dataKeyY2={'merchant'}
                                            stroke
                                            dataKey={[
                                                { name: 'merchant', strokeColor: '#1976d2', fillColor: '#e3f2fd' },
                                                { name: 'menu', strokeColor: '#2e7d32', fillColor: '#e8f5e9' }
                                            ]}
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={5}>
                                        No hay datos suficientes para mostrar la gráfica.
                                    </Typography>
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Top Productos */}
                <Grid item xs={12} lg={4}>
                    <Card {...cardStyle} sx={{ ...cardStyle.sx, display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Avatar variant='rounded' sx={{ width: 40, height: 40, bgcolor: '#fff3e0', color: '#ed6c02', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faTags} size="sm" />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ color: '#222' }}>Top Productos</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>Los más vistos (7 días)</Typography>
                            </Box>
                        </Stack>
                        
                        <Divider sx={{ mb: 1, borderColor: '#e0e0e0' }} />

                        {topProducts.length === 0 ? (
                            <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                                <Typography variant="body2" color="text.secondary">No hay visualizaciones recientes.</Typography>
                            </Box>
                        ) : (
                            <List disablePadding sx={{ flexGrow: 1 }}>
                                {topProducts.map((item, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem alignItems="center" disablePadding sx={{ py: 1.5 }}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    variant="rounded"
                                                    sx={{ width: 44, height: 44, borderRadius: 2 }}
                                                    alt={item.product_name}
                                                    src={`${imagesURL}/productos/${item.product_image}`}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                sx={{ ml: 1.5, my: 0 }}
                                                primary={item.product_name}
                                                primaryTypographyProps={{ fontWeight: 600, variant: 'body2', color: '#333', noWrap: true }}
                                                secondary={`${item.total_views} visualizaciones`}
                                                secondaryTypographyProps={{ variant: 'caption', color: '#888', fontWeight: 500 }}
                                            />
                                        </ListItem>
                                        {index !== topProducts.length - 1 && <Divider component="li" sx={{ borderColor: '#f0f0f0' }} />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}